import React from 'react';
import { DNAText, DNATextProps } from '@alucio/lux-ui';
import ReactMarkdown from 'react-markdown';

const AI_DISCLAIMER_TEXT = 'AI responses may include mistakes. Please review before proceeding.';

type DNATextChildren = React.ComponentProps<typeof DNAText>['children'];

const renderUnderline = (children: React.ReactNode, textProps: DNATextProps) => {
  const renderedChildren: React.ReactNode[] = [];
  let underlinedChildren: React.ReactNode[] = [];
  let isUnderlined = false;

  React.Children.toArray(children).forEach((child, index) => {
    if (child === '<u>') {
      isUnderlined = true;
      underlinedChildren = [];
      return;
    }

    if (child === '</u>' && isUnderlined) {
      renderedChildren.push(
        <DNAText
          key={`underline-${index}`}
          {...textProps}
          style={[textProps.style, { textDecorationLine: 'underline' }]}
        >
          {underlinedChildren as DNATextChildren}
        </DNAText>,
      );
      isUnderlined = false;
      return;
    }

    if (isUnderlined) {
      underlinedChildren.push(child);
      return;
    }

    renderedChildren.push(child);
  });

  if (isUnderlined) {
    renderedChildren.push('<u>', ...underlinedChildren);
  }

  return renderedChildren;
};

const getMarkdownRenderers = (textProps: DNATextProps) => ({
  p: (props) => (
    <DNAText {...textProps}>
      <>
        {renderUnderline(props.children, textProps) as DNATextChildren}
        {props.index < props.siblingCount - 1 ? '\n\n' : ''}
      </>
    </DNAText>
  ),
  strong: (props) => (
    <DNAText {...textProps} bold>
      {props.children}
    </DNAText>
  ),
  em: (props) => (
    <DNAText {...textProps} italic>
      {props.children}
    </DNAText>
  ),
  br: () => null,
});

export const AIDisclaimer: React.FC<DNATextProps> = ({
  children = AI_DISCLAIMER_TEXT,
  c1 = true,
  status = 'tertiary',
  ...restProps
}) => {
  const textProps = {
    c1,
    status,
    ...restProps,
  };

  if (typeof children === 'string') {
    return (
      <ReactMarkdown includeElementIndex components={getMarkdownRenderers(textProps)}>
        {children}
      </ReactMarkdown>
    );
  }

  return (
    <DNAText {...textProps}>
      {children}
    </DNAText>
  );
};

export default AIDisclaimer;
